import React, { useEffect, useState } from 'react';
import { Plus, Check, X, Download, Trash2, Paperclip } from 'lucide-react';
import { api, buildQuery } from '../lib/api';
import type { MonthlyExpense } from '../types';
import { formatDate, formatDateTime, formatFileSize } from '../lib/format';
import { useUpload } from '../lib/useUpload';
import {
  Spinner, EmptyState, Modal, Alert, StatusBadge, ConfirmDialog, UploadProgress,
  inputClass, labelClass, primaryBtn, secondaryBtn,
} from './ui';

interface Props {
  role: string;
}

export default function MonthlyExpensesView({ role }: Props) {
  const canReview = role === 'admin' || role === 'account';
  const canUpload = role === 'staff' || role === 'manager' || role === 'admin';

  const [items, setItems] = useState<MonthlyExpense[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [excel, setExcel] = useState<File | null>(null);
  const [receipts, setReceipts] = useState<File[]>([]);
  const upload = useUpload();
  const [error, setError] = useState('');
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const load = () => {
    setLoading(true);
    api
      .get<MonthlyExpense[]>(`/monthly-expenses${buildQuery({ status: statusFilter })}`)
      .then(setItems)
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(load, [statusFilter]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    upload.reset();
    setError('');
    try {
      const fd = new FormData();
      fd.append('month', month);
      fd.append('title', title);
      if (description) fd.append('description', description);
      if (excel) fd.append('excel', excel);
      receipts.forEach((r) => fd.append('receipts', r));
      await upload.run('/monthly-expenses', fd);
      setModalOpen(false);
      setMonth(new Date().toISOString().slice(0, 7));
      setTitle('');
      setDescription('');
      setExcel(null);
      setReceipts([]);
      load();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const setStatus = async (id: number, status: string) => {
    try {
      await api.patch(`/monthly-expenses/${id}/status`, { status });
      load();
    } catch {
      // ignore
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await api.delete(`/monthly-expenses/${id}`);
      setDeleteId(null);
      load();
    } catch {
      setDeleteId(null);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-6">
        <h1 className="text-2xl font-bold">Monthly Expenses</h1>
        {canUpload && (
          <button onClick={() => { setError(''); setModalOpen(true); }} className={primaryBtn}>
            <Plus className="h-4 w-4" /> Upload Monthly Expenses
          </button>
        )}
      </div>

      {canReview && (
        <div className="mb-4">
          <select
            className="w-48 px-4 py-2 border border-gray-300 rounded-lg text-sm"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All statuses</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      )}

      {loading ? (
        <Spinner label="Loading monthly expenses..." />
      ) : items.length === 0 ? (
        <EmptyState message="No monthly expense uploads found." />
      ) : (
        <div className="grid gap-4">
          {items.map((m) => (
            <div key={m.id} className="bg-white p-5 rounded-lg shadow-lg">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-3 flex-wrap mb-1">
                    <h3 className="font-semibold">{m.title || `Monthly Expenses ${formatDate(m.month)}`}</h3>
                    <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">{formatDate(m.month)}</span>
                    <StatusBadge status={m.status} />
                  </div>
                  <p className="text-xs text-gray-500 mb-2">
                    Uploaded by {m.user_name} · {formatDateTime(m.created_at)}
                    {m.reviewed_by_name ? ` · Reviewed by ${m.reviewed_by_name}${m.reviewed_at ? ` on ${formatDate(m.reviewed_at)}` : ''}` : ''}
                  </p>
                  {m.description && <p className="text-sm text-gray-600 whitespace-pre-line mb-3">{m.description}</p>}

                  <div className="flex flex-wrap gap-2">
                    {m.excel && (
                      <a
                        href={m.excel.url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-50 text-blue-700 text-xs hover:bg-blue-100"
                      >
                        <Download className="h-3.5 w-3.5" /> Excel Sheet: {m.excel.filename}
                      </a>
                    )}
                    {m.receipts.map((r) => (
                      <a
                        key={r.id}
                        href={r.url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 text-xs hover:bg-gray-200"
                      >
                        <Paperclip className="h-3.5 w-3.5" />
                        <span className="max-w-[160px] truncate">{r.filename}</span>
                        <span className="text-gray-400">{formatFileSize(r.size)}</span>
                      </a>
                    ))}
                  </div>
                </div>

                <div className="shrink-0 space-y-2">
                  {canReview && m.status === 'pending' && (
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => setStatus(m.id, 'approved')}
                        className="px-3 py-2 bg-green-600 text-white rounded-lg text-xs flex items-center gap-1.5 hover:bg-green-700"
                      >
                        <Check className="h-3.5 w-3.5" /> Approve
                      </button>
                      <button
                        onClick={() => setStatus(m.id, 'rejected')}
                        className="px-3 py-2 bg-red-600 text-white rounded-lg text-xs flex items-center gap-1.5 hover:bg-red-700"
                      >
                        <X className="h-3.5 w-3.5" /> Reject
                      </button>
                    </div>
                  )}
                  {(canReview || m.user_id) && (
                    <button onClick={() => setDeleteId(m.id)} className="p-2 rounded-lg hover:bg-red-50 text-red-500" title="Delete">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title="Upload Monthly Expenses">
        {error && <Alert>{error}</Alert>}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Month *</label>
              <input type="month" required className={inputClass} value={month} onChange={(e) => setMonth(e.target.value)} />
            </div>
            <div>
              <label className={labelClass}>Title *</label>
              <input required className={inputClass} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. June 2026 expenses" />
            </div>
          </div>
          <div>
            <label className={labelClass}>Description</label>
            <textarea className={inputClass} rows={3} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Notes about this month's expenses..." />
          </div>
          <div>
            <label className={labelClass}>Excel Sheet</label>
            <input type="file" accept=".xlsx,.xls,.csv" className={inputClass} onChange={(e) => setExcel(e.target.files?.[0] || null)} />
            <p className="text-xs text-gray-400 mt-1">Upload the month's expense breakdown as an Excel sheet.</p>
          </div>
          <div>
            <label className={labelClass}>Receipts *</label>
            <input
              type="file"
              multiple
              required
              accept="image/jpeg,image/png,image/gif,image/webp,.pdf"
              className={inputClass}
              onChange={(e) => setReceipts(Array.from(e.target.files || []).slice(0, 10))}
            />
            <p className="text-xs text-gray-400 mt-1">Receipts are required. Attach at least one receipt supporting this month's expenses.</p>
            {receipts.length > 0 && (
              <p className="text-xs text-primary-dark mt-1">{receipts.length} receipt{receipts.length > 1 ? 's' : ''} selected</p>
            )}
          </div>
          <UploadProgress
            percent={upload.percent}
            phase={upload.phase}
            error={upload.error}
            onCancel={upload.cancel}
          />
          <div className="flex justify-end space-x-3">
            <button type="button" onClick={() => setModalOpen(false)} className={secondaryBtn}>Cancel</button>
            <button type="submit" disabled={upload.busy} className={primaryBtn}>
              {upload.phase === 'uploading'
                ? `Uploading ${upload.percent}%`
                : upload.phase === 'processing' ? 'Processing...' : 'Upload'}
            </button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={() => deleteId && handleDelete(deleteId)}
        title="Delete Monthly Expense"
        message="Are you sure you want to delete this monthly expense upload and its files?"
      />
    </div>
  );
}
